import { useCallback, useEffect, useState } from "react";
import { useParams } from "wouter";

import { apiClient } from "../api/client.js";
import { useAuth } from "../auth/AuthContext.js";
import {
  emptyTierDraft,
  PricingHistoryList,
  type PricingRevision,
  rateToTierDraft,
  type SessionRate,
  type TierDraft,
  TierEditor,
  TierReadonlyList,
  tiersToRatePayload,
} from "../components/TierEditor.js";
import { isFleetAdmin } from "../hooks/useFleetAccess.js";
import { useFleetOptions } from "../hooks/useFleetOptions.js";
import { VenueGameSettings } from "./VenueGameSettings.js";

interface RecurringPeriod {
  day: number;
  start: string;
  end: string;
}

interface AdhocPeriod {
  starts_at: string;
  expires_at: string;
}

interface VenueConfig {
  free_play_times?: {
    recurring?: RecurringPeriod[];
    adhoc?: AdhocPeriod[];
  };
  scheduled_downtime?: {
    recurring?: RecurringPeriod[];
    adhoc?: AdhocPeriod[];
  };
}

interface Venue {
  id: string;
  fleet_id: string;
  name: string;
  slug: string;
  address?: string;
  status: string;
  timezone?: string;
  config?: VenueConfig;
  use_custom_pricing: boolean;
  is_public: boolean;
  created_at: string;
  updated_at: string;
}

interface VenueMember {
  id: string;
  user_id: string;
  email: string;
  display_name: string;
  role: string;
  created_at: string;
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

const DAY_NAMES = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

function statusBadge(status: string) {
  switch (status) {
    case "active":
      return (
        <span className="inline-block px-2 py-0.5 text-xs font-medium rounded-full bg-green-500/20 text-green-400">
          Active
        </span>
      );
    case "inactive":
      return (
        <span className="inline-block px-2 py-0.5 text-xs font-medium rounded-full bg-gray-500/20 text-gray-400">
          Inactive
        </span>
      );
    default:
      return (
        <span className="inline-block px-2 py-0.5 text-xs font-medium rounded-full bg-gray-500/20 text-gray-400">
          {status.charAt(0).toUpperCase() + status.slice(1)}
        </span>
      );
  }
}

function roleBadge(role: string) {
  switch (role) {
    case "admin":
      return (
        <span className="inline-block px-2 py-0.5 text-xs font-medium rounded-full bg-blue-500/20 text-blue-400">
          Admin
        </span>
      );
    case "member":
      return (
        <span className="inline-block px-2 py-0.5 text-xs font-medium rounded-full bg-gray-500/20 text-gray-400">
          Member
        </span>
      );
    default:
      return (
        <span className="inline-block px-2 py-0.5 text-xs font-medium rounded-full bg-gray-500/20 text-gray-400">
          {role.charAt(0).toUpperCase() + role.slice(1)}
        </span>
      );
  }
}

function PenIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="w-4 h-4"
      aria-hidden="true"
    >
      <path d="M12 20h9" />
      <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z" />
    </svg>
  );
}

export function VenueDetail() {
  const { user } = useAuth();
  const { slug } = useParams<{ slug: string }>();

  const [venue, setVenue] = useState<Venue | null>(null);
  const [members, setMembers] = useState<VenueMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [membersLoading, setMembersLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  const canAdmin = user && venue ? isFleetAdmin(user, venue.fleet_id) : false;
  const { options: fleetOptions } = useFleetOptions();
  const fleetName = venue
    ? (fleetOptions.find((f) => f.id === venue.fleet_id)?.name ?? null)
    : null;

  const [inviteEmail, setInviteEmail] = useState("");
  const [inviting, setInviting] = useState(false);
  const [inviteError, setInviteError] = useState<string | null>(null);
  const [inviteSuccess, setInviteSuccess] = useState<string | null>(null);

  const [isEditingName, setIsEditingName] = useState(false);
  const [editName, setEditName] = useState("");
  const [savingName, setSavingName] = useState(false);
  const [nameError, setNameError] = useState<string | null>(null);
  const [nameSuccess, setNameSuccess] = useState<string | null>(null);

  const [savingPublic, setSavingPublic] = useState(false);
  const [publicError, setPublicError] = useState<string | null>(null);
  const [publicSuccess, setPublicSuccess] = useState<string | null>(null);

  const [overrideOn, setOverrideOn] = useState(false);
  const [pricingTiers, setPricingTiers] = useState<TierDraft[]>([]);
  const [fleetRateTiers, setFleetRateTiers] = useState<TierDraft[]>([]);
  const [pricingHistory, setPricingHistory] = useState<PricingRevision[]>([]);
  const [pricingLoading, setPricingLoading] = useState(true);
  const [pricingSaving, setPricingSaving] = useState(false);
  const [pricingError, setPricingError] = useState<string | null>(null);
  const [pricingSuccess, setPricingSuccess] = useState<string | null>(null);

  const startEditingName = useCallback(() => {
    if (!venue) return;
    setEditName(venue.name);
    setNameError(null);
    setNameSuccess(null);
    setIsEditingName(true);
  }, [venue]);

  const cancelEditingName = useCallback(() => {
    setIsEditingName(false);
    setNameError(null);
    setNameSuccess(null);
  }, []);

  const fetchVenue = useCallback(async () => {
    if (!slug) return;
    try {
      const res = await apiClient["/api/v1/admin/venues/{slug}"]["get"]({
        parameters: { slug },
        body: null,
      });
      if (res.code === 200) {
        setVenue(res.body as Venue);
      } else if (res.code === 404) {
        setNotFound(true);
      }
    } catch {
      // Ignore
    } finally {
      setLoading(false);
    }
  }, [slug]);

  const fetchMembers = useCallback(async () => {
    if (!slug) return;
    try {
      const res = await apiClient["/api/v1/admin/venues/{slug}/members"]["get"]({
        parameters: { slug },
        body: null,
      });
      if (res.code === 200) {
        setMembers(res.body as VenueMember[]);
      }
    } catch {
      // Ignore
    } finally {
      setMembersLoading(false);
    }
  }, [slug]);

  const fetchPricing = useCallback(async () => {
    if (!slug) return;
    try {
      const res = await apiClient["/api/v1/admin/venues/{slug}/pricing"]["get"]({
        parameters: { slug },
        body: null,
      });
      if (res.code === 200) {
        const body = res.body as {
          use_custom_pricing: boolean;
          rates: SessionRate[];
          fleet_rates: SessionRate[];
          history: PricingRevision[];
        };
        setOverrideOn(body.use_custom_pricing);
        const active = body.rates.filter((r) => r.is_active);
        setPricingTiers(active.length > 0 ? active.map(rateToTierDraft) : [emptyTierDraft(0)]);
        setFleetRateTiers(body.fleet_rates.map(rateToTierDraft));
        setPricingHistory(body.history ?? []);
      }
    } catch {
      // Ignore
    } finally {
      setPricingLoading(false);
    }
  }, [slug]);

  useEffect(() => {
    fetchVenue();
    fetchMembers();
    fetchPricing();
  }, [fetchVenue, fetchMembers, fetchPricing]);

  useEffect(() => {
    if (!venue || window.location.hash !== "#pricing") return;
    document.getElementById("pricing")?.scrollIntoView({ block: "start" });
  }, [venue?.id]);

  const handleSavePricing = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (!slug) return;
      setPricingError(null);
      setPricingSuccess(null);
      try {
        setPricingSaving(true);
        const body: { use_custom_pricing: boolean; rates?: ReturnType<typeof tiersToRatePayload> } =
          {
            use_custom_pricing: overrideOn,
          };
        if (overrideOn) {
          body.rates = tiersToRatePayload(pricingTiers);
        }
        const res = await apiClient["/api/v1/admin/venues/{slug}/pricing"]["put"]({
          parameters: { slug },
          body: body as never,
        });
        if (res.code === 200) {
          setPricingSuccess(
            overrideOn ? "Venue pricing override saved." : "Venue is using fleet pricing.",
          );
          await fetchPricing();
          await fetchVenue();
        } else {
          const errBody = res.body as { message?: string };
          setPricingError(errBody.message ?? "Failed to save pricing");
        }
      } catch (err) {
        setPricingError(err instanceof Error ? err.message : "Network error");
      } finally {
        setPricingSaving(false);
      }
    },
    [slug, overrideOn, pricingTiers, fetchPricing, fetchVenue],
  );

  const handleInvite = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (!slug || !inviteEmail.trim()) return;

      setInviting(true);
      setInviteError(null);
      setInviteSuccess(null);

      try {
        const res = await apiClient["/api/v1/admin/venues/{slug}/members"]["post"]({
          parameters: { slug },
          body: { email: inviteEmail.trim() },
        });
        if (res.code === 201) {
          setInviteSuccess(`Invited ${inviteEmail.trim()}`);
          setInviteEmail("");
          fetchMembers();
        } else {
          const body = res.body as { message?: string };
          setInviteError(body.message ?? "Failed to invite member");
        }
      } catch {
        setInviteError("Network error");
      } finally {
        setInviting(false);
      }
    },
    [slug, inviteEmail, fetchMembers],
  );

  const handleRename = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (!slug || !editName.trim()) return;

      setSavingName(true);
      setNameError(null);
      setNameSuccess(null);

      try {
        const res = await apiClient["/api/v1/admin/venues/{slug}"]["patch"]({
          parameters: { slug },
          body: { name: editName.trim() },
        });
        if (res.code === 200) {
          setNameSuccess("Venue name updated");
          setIsEditingName(false);
          setEditName("");
          fetchVenue();
        } else {
          const body = res.body as { message?: string };
          setNameError(body.message ?? "Failed to update venue name");
        }
      } catch {
        setNameError("Network error");
      } finally {
        setSavingName(false);
      }
    },
    [slug, editName, fetchVenue],
  );

  const handleTogglePublic = useCallback(
    async (next: boolean) => {
      if (!slug || !venue) return;

      setSavingPublic(true);
      setPublicError(null);
      setPublicSuccess(null);

      try {
        const res = await apiClient["/api/v1/admin/venues/{slug}"]["patch"]({
          parameters: { slug },
          body: { is_public: next },
        });
        if (res.code === 200) {
          setVenue(res.body as Venue);
          setPublicSuccess(
            next
              ? "Venue is public — included in analytics."
              : "Venue is not public — excluded from analytics.",
          );
        } else {
          const body = res.body as { message?: string };
          setPublicError(body.message ?? "Failed to update public flag");
        }
      } catch {
        setPublicError("Network error");
      } finally {
        setSavingPublic(false);
      }
    },
    [slug, venue],
  );

  if (loading) {
    return (
      <main className="p-6">
        <p className="text-text-muted text-sm">Loading venue...</p>
      </main>
    );
  }

  if (notFound) {
    return (
      <main className="p-6">
        <div className="mb-6">
          <a
            href="/venues"
            className="text-sm text-text-muted hover:text-accent transition-colors no-underline"
          >
            &larr; Back to Venues
          </a>
        </div>
        <div className="bg-surface-raised border border-border rounded-lg p-6 text-center">
          <p className="text-text-secondary">Venue not found.</p>
        </div>
      </main>
    );
  }

  return (
    <main className="p-6">
      <div className="mb-6">
        <a
          href="/venues"
          className="text-sm text-text-muted hover:text-accent transition-colors no-underline"
        >
          &larr; Back to Venues
        </a>
      </div>

      {venue && (
        <div className="bg-surface-raised border border-border rounded-lg p-6 mb-6">
          <div className="flex items-center gap-3 mb-2">
            {isEditingName ? (
              <form onSubmit={handleRename} className="flex items-center gap-2 flex-1">
                <label className="sr-only" htmlFor="venue-name">
                  Venue name
                </label>
                <input
                  id="venue-name"
                  type="text"
                  value={editName}
                  onChange={(e) => {
                    setEditName(e.target.value);
                    setNameError(null);
                    setNameSuccess(null);
                  }}
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === "Escape") cancelEditingName();
                  }}
                  className="px-3 py-1.5 rounded-md bg-surface border border-accent text-text-primary placeholder:text-text-muted focus:outline-none focus:border-accent transition-colors"
                />
                <button
                  type="submit"
                  disabled={savingName || !editName.trim()}
                  className="px-3 py-1.5 text-sm font-medium rounded-md bg-accent text-white hover:bg-accent-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                >
                  {savingName ? "Saving..." : "Save"}
                </button>
                <button
                  type="button"
                  onClick={cancelEditingName}
                  disabled={savingName}
                  className="px-3 py-1.5 text-sm font-medium rounded-md border border-border text-text-secondary hover:bg-surface-raised transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                >
                  Cancel
                </button>
              </form>
            ) : (
              <>
                <h1 className="text-2xl font-bold text-text-primary">{venue.name}</h1>
                {canAdmin && (
                  <button
                    type="button"
                    onClick={startEditingName}
                    title="Rename venue"
                    aria-label="Rename venue"
                    className="p-1.5 rounded-md text-text-muted hover:text-accent hover:bg-surface transition-colors"
                  >
                    <PenIcon />
                  </button>
                )}
              </>
            )}
            {statusBadge(venue.status)}
          </div>
          {nameError && <p className="text-sm text-red-400 mb-1">{nameError}</p>}
          {nameSuccess && <p className="text-sm text-green-400 mb-1">{nameSuccess}</p>}
          <div className="text-sm text-text-muted space-y-1">
            <p>
              Slug: <span className="font-mono text-text-secondary">{venue.slug}</span>
            </p>
            {fleetName && (
              <p>
                Fleet:{" "}
                <a
                  href={`/fleets/${venue.fleet_id}`}
                  className="text-text-secondary hover:text-accent transition-colors no-underline"
                >
                  {fleetName}
                </a>
              </p>
            )}
            <p>
              Fleet ID: <span className="font-mono text-text-secondary">{venue.fleet_id}</span>
            </p>
            {venue.address && (
              <p>
                Address: <span className="text-text-secondary">{venue.address}</span>
              </p>
            )}
            <p>
              Created: <span className="text-text-secondary">{formatDate(venue.created_at)}</span>
            </p>
            {canAdmin ? (
              <label className="flex items-center gap-2 cursor-pointer">
                <span>Public:</span>
                <input
                  type="checkbox"
                  checked={venue.is_public !== false}
                  disabled={savingPublic}
                  onChange={(e) => handleTogglePublic(e.target.checked)}
                  className="accent-accent"
                />
                <span className="text-text-secondary">
                  When off, analytics reporting excludes this venue. Historical events are kept.
                  Turn off for testing venues only.
                </span>
              </label>
            ) : (
              <p>
                Public:{" "}
                <span className="text-text-secondary">
                  {venue.is_public !== false ? "Yes" : "No"}
                </span>
                {venue.is_public === false && " — excluded from analytics"}
              </p>
            )}
            {publicError && <p className="text-sm text-red-400">{publicError}</p>}
            {publicSuccess && <p className="text-sm text-green-400">{publicSuccess}</p>}
          </div>
        </div>
      )}

      {venue && <VenueGameSettings key={venue.id} slug={venue.slug} canEdit={!!canAdmin} />}
      <section className="mb-6">
        <h2 className="text-lg font-semibold text-text-primary mb-4">Members</h2>

        {canAdmin && (
          <>
            <form onSubmit={handleInvite} className="flex items-end gap-3 mb-4">
              <div className="flex-1">
                <label className="block text-sm text-text-secondary mb-1">Invite Member</label>
                <input
                  type="email"
                  value={inviteEmail}
                  onChange={(e) => {
                    setInviteEmail(e.target.value);
                    setInviteError(null);
                    setInviteSuccess(null);
                  }}
                  placeholder="user@example.com"
                  required
                  className="w-full px-3 py-2 rounded-md bg-surface border border-border text-text-primary placeholder:text-text-muted focus:outline-none focus:border-accent transition-colors"
                />
              </div>
              <button
                type="submit"
                disabled={inviting || !inviteEmail.trim()}
                className="px-4 py-2 text-sm rounded-md bg-accent text-white hover:bg-accent-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {inviting ? "Inviting..." : "Invite"}
              </button>
            </form>

            {inviteError && <p className="text-sm text-red-400 mb-3">{inviteError}</p>}
            {inviteSuccess && <p className="text-sm text-green-400 mb-3">{inviteSuccess}</p>}
          </>
        )}

        {membersLoading && <p className="text-text-muted text-sm">Loading members...</p>}

        {!membersLoading && members.length === 0 && (
          <div className="bg-surface-raised border border-border rounded-lg p-6 text-center">
            <p className="text-text-secondary">No members yet.</p>
            <p className="text-text-muted text-sm mt-1">
              Invite someone by entering their email above.
            </p>
          </div>
        )}

        {!membersLoading && members.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-border">
                  <th className="pb-2 text-sm font-medium text-text-muted">Email</th>
                  <th className="pb-2 text-sm font-medium text-text-muted">Display Name</th>
                  <th className="pb-2 text-sm font-medium text-text-muted">Role</th>
                  <th className="pb-2 text-sm font-medium text-text-muted">Joined</th>
                </tr>
              </thead>
              <tbody>
                {members.map((m) => (
                  <tr key={m.id} className="border-b border-border/50">
                    <td className="py-3">
                      <span className="text-sm text-text-primary">{m.email}</span>
                    </td>
                    <td className="py-3">
                      <span className="text-sm text-text-secondary">
                        {m.display_name || "\u2014"}
                      </span>
                    </td>
                    <td className="py-3">{roleBadge(m.role)}</td>
                    <td className="py-3">
                      <span className="text-sm text-text-muted">{formatDate(m.created_at)}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {venue && (
        <section className="mb-6">
          <h2 className="text-lg font-semibold text-text-primary mb-4">Scheduled Downtime</h2>
          <div className="bg-surface-raised border border-border rounded-lg p-6">
            <h3 className="text-sm font-medium text-text-secondary mb-2">Recurring Schedule</h3>
            {venue.config?.scheduled_downtime?.recurring &&
            venue.config.scheduled_downtime.recurring.length > 0 ? (
              <div className="overflow-x-auto mb-6">
                <table className="w-full text-left">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="pb-2 text-sm font-medium text-text-muted">Day</th>
                      <th className="pb-2 text-sm font-medium text-text-muted">Start</th>
                      <th className="pb-2 text-sm font-medium text-text-muted">End</th>
                    </tr>
                  </thead>
                  <tbody>
                    {venue.config.scheduled_downtime.recurring.map((period, i) => (
                      <tr key={i} className="border-b border-border/50">
                        <td className="py-3 text-sm text-text-primary">
                          {DAY_NAMES[period.day] ?? `Day ${period.day}`}
                        </td>
                        <td className="py-3 text-sm text-text-secondary font-mono">
                          {period.start}
                        </td>
                        <td className="py-3 text-sm text-text-secondary font-mono">{period.end}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-sm text-text-muted mb-6">No scheduled downtime configured.</p>
            )}

            <h3 className="text-sm font-medium text-text-secondary mb-2">Ad-hoc Periods</h3>
            {(() => {
              const now = new Date();
              const activePeriods = (venue.config?.scheduled_downtime?.adhoc ?? []).filter(
                (p) => new Date(p.expires_at) > now,
              );
              if (activePeriods.length === 0) {
                return <p className="text-sm text-text-muted">No ad-hoc downtime periods.</p>;
              }
              return (
                <div className="overflow-x-auto">
                  <table className="w-full text-left">
                    <thead>
                      <tr className="border-b border-border">
                        <th className="pb-2 text-sm font-medium text-text-muted">Starts</th>
                        <th className="pb-2 text-sm font-medium text-text-muted">Expires</th>
                      </tr>
                    </thead>
                    <tbody>
                      {activePeriods.map((period, i) => (
                        <tr key={i} className="border-b border-border/50">
                          <td className="py-3 text-sm text-text-secondary">
                            {formatDateTime(period.starts_at)}
                          </td>
                          <td className="py-3 text-sm text-text-secondary">
                            {formatDateTime(period.expires_at)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              );
            })()}
          </div>
        </section>
      )}

      {venue && (
        <section className="mb-6">
          <h2 className="text-lg font-semibold text-text-primary mb-4">Free Play Times</h2>
          <div className="bg-surface-raised border border-border rounded-lg p-6">
            <div className="text-sm text-text-muted mb-4">
              Timezone:{" "}
              <span className="font-mono text-text-secondary">{venue.timezone ?? "Not set"}</span>
            </div>

            <h3 className="text-sm font-medium text-text-secondary mb-2">Recurring Schedule</h3>
            {venue.config?.free_play_times?.recurring &&
            venue.config.free_play_times.recurring.length > 0 ? (
              <div className="overflow-x-auto mb-6">
                <table className="w-full text-left">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="pb-2 text-sm font-medium text-text-muted">Day</th>
                      <th className="pb-2 text-sm font-medium text-text-muted">Start</th>
                      <th className="pb-2 text-sm font-medium text-text-muted">End</th>
                    </tr>
                  </thead>
                  <tbody>
                    {venue.config.free_play_times.recurring.map((period, i) => (
                      <tr key={i} className="border-b border-border/50">
                        <td className="py-3 text-sm text-text-primary">
                          {DAY_NAMES[period.day] ?? `Day ${period.day}`}
                        </td>
                        <td className="py-3 text-sm text-text-secondary font-mono">
                          {period.start}
                        </td>
                        <td className="py-3 text-sm text-text-secondary font-mono">{period.end}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-sm text-text-muted mb-6">
                No recurring free play times configured.
              </p>
            )}

            <h3 className="text-sm font-medium text-text-secondary mb-2">Ad-hoc Periods</h3>
            {(() => {
              const now = new Date();
              const activePeriods = (venue.config?.free_play_times?.adhoc ?? []).filter(
                (p) => new Date(p.expires_at) > now,
              );
              if (activePeriods.length === 0) {
                return <p className="text-sm text-text-muted">No ad-hoc free play times.</p>;
              }
              return (
                <div className="overflow-x-auto">
                  <table className="w-full text-left">
                    <thead>
                      <tr className="border-b border-border">
                        <th className="pb-2 text-sm font-medium text-text-muted">Starts</th>
                        <th className="pb-2 text-sm font-medium text-text-muted">Expires</th>
                      </tr>
                    </thead>
                    <tbody>
                      {activePeriods.map((period, i) => (
                        <tr key={i} className="border-b border-border/50">
                          <td className="py-3 text-sm text-text-secondary">
                            {formatDateTime(period.starts_at)}
                          </td>
                          <td className="py-3 text-sm text-text-secondary">
                            {formatDateTime(period.expires_at)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              );
            })()}
          </div>
        </section>
      )}

      {venue && (
        <section id="pricing" className="mb-6 scroll-mt-6">
          <h2 className="text-lg font-semibold text-text-primary mb-4">Pricing</h2>
          <div className="bg-surface-raised border border-border rounded-lg p-6 space-y-4">
            {pricingLoading ? (
              <p className="text-sm text-text-muted">Loading pricing...</p>
            ) : (
              <>
                {canAdmin ? (
                  <form onSubmit={handleSavePricing} className="space-y-4 max-w-2xl">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={overrideOn}
                        onChange={(e) => {
                          const on = e.target.checked;
                          setOverrideOn(on);
                          setPricingSuccess(null);
                          if (on && pricingTiers.every((t) => !t.amount)) {
                            setPricingTiers(
                              fleetRateTiers.length > 0
                                ? fleetRateTiers.map((t) => ({ ...t, id: undefined }))
                                : [emptyTierDraft(0)],
                            );
                          }
                        }}
                        className="accent-accent"
                      />
                      <span className="text-sm text-text-primary">Override fleet pricing</span>
                    </label>

                    {overrideOn ? (
                      <TierEditor
                        tiers={pricingTiers}
                        onChange={(next) => {
                          setPricingTiers(next);
                          setPricingSuccess(null);
                        }}
                        disabled={pricingSaving}
                      />
                    ) : (
                      <div>
                        <p className="text-sm text-text-muted mb-3">Using fleet defaults.</p>
                        <TierReadonlyList tiers={fleetRateTiers} />
                      </div>
                    )}

                    {pricingError && <p className="text-sm text-red-400">{pricingError}</p>}
                    {pricingSuccess && <p className="text-sm text-green-400">{pricingSuccess}</p>}

                    <button
                      type="submit"
                      disabled={pricingSaving}
                      className="px-4 py-2 text-sm rounded-md bg-accent text-white hover:bg-accent-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {pricingSaving ? "Saving..." : "Save Pricing"}
                    </button>
                  </form>
                ) : overrideOn ? (
                  <div className="max-w-2xl">
                    <p className="text-sm text-text-muted mb-3">
                      This venue overrides fleet pricing.
                    </p>
                    <TierReadonlyList tiers={pricingTiers} />
                  </div>
                ) : (
                  <div className="max-w-2xl">
                    <p className="text-sm text-text-muted mb-3">Using fleet defaults.</p>
                    <TierReadonlyList tiers={fleetRateTiers} />
                  </div>
                )}

                <PricingHistoryList history={pricingHistory} />
              </>
            )}
          </div>
        </section>
      )}
    </main>
  );
}
